"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Segmented, SegmentedItem } from "@/components/ui/segmented";
import { BackLink } from "@/components/shared/back-link";
import { ArrowRightIcon, Emphasis, OptionalHint, STEP_CTA_CLASS, StepHeader } from "@/components/shared/step-header";
import { FieldError, FieldHelp } from "@/components/shared/field";
import { lookupCep } from "@/lib/onboarding/cep-lookup";
import { maskZipCode } from "@/lib/onboarding/masks";
import {
  addressStepSchema,
  ADDRESS_TYPE_LABELS,
  ADDRESS_TYPES,
  type AddressStepValues,
} from "@/lib/onboarding/onboarding-schemas";
import { onlyDigits } from "@/lib/strings";

interface AddressStepFormProps {
  isPending: boolean;
  errorMessage: string | null;
  /** Pre-fills the form when navigating back (e.g. after a register error bounced to signup and back). */
  initialValues?: AddressStepValues | null;
  onBack?: () => void;
  onSubmit: (values: AddressStepValues) => void;
  /**
   * Renders the "Uso deste endereço" section (billing/delivery chips). Defaults to `true`.
   * When `false`, the section is hidden and both usage flags are submitted as `true`, so the
   * schema's "at least one usage" rule always passes.
   */
  showUsage?: boolean;
  /** Overrides the step's words; every field left out keeps the host's (`DEFAULT_ADDRESS_COPY`). */
  copy?: AddressStepCopy;
}

/** The address step's words that another flow may replace. */
export interface AddressStepCopy {
  backLabel?: string;
  eyebrow?: string;
  title?: ReactNode;
  lede?: string;
  submitLabel?: string;
  pendingLabel?: string;
}

/** The host's words for the address step. */
const DEFAULT_ADDRESS_COPY = {
  backLabel: "voltar aos dados",
  eyebrow: "Cadastro · endereço",
  title: (
    <>
      Informe seu <Emphasis>endereço</Emphasis>
    </>
  ),
  lede: "Ele será a nossa referência para a entrega dos presentes que você resgatar.",
  submitLabel: "Criar cadastro e continuar",
  pendingLabel: "Continuando...",
} as const satisfies Required<AddressStepCopy>;

function SectionEyebrow({ children, first }: { children: ReactNode; first?: boolean }) {
  return (
    <div className={`flex items-center gap-3.5 ${first ? "mt-0" : "mt-10"} mb-[22px]`}>
      <span className="text-[12px] font-medium uppercase tracking-[0.16em] text-fg-subtle">
        {children}
      </span>
      <span className="h-px flex-1 bg-[var(--tb-hairline)]" />
    </div>
  );
}

interface UsoChipProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
}

function UsoChip({ checked, onCheckedChange, children }: UsoChipProps) {
  return (
    <label
      className={`flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-[var(--tb-radius-xs)] border px-4 py-[13px] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] ${
        checked ? "border-primary" : "border-[color:var(--tb-border)]"
      }`}
    >
      <Checkbox checked={checked} onCheckedChange={(next) => onCheckedChange(next === true)} className="size-5" />
      <span className="truncate text-[15px] text-foreground">{children}</span>
    </label>
  );
}

export function AddressStepForm({
  isPending,
  errorMessage,
  initialValues,
  onBack,
  onSubmit,
  showUsage = true,
  copy,
}: AddressStepFormProps) {
  const backLabel = copy?.backLabel ?? DEFAULT_ADDRESS_COPY.backLabel;
  const eyebrow = copy?.eyebrow ?? DEFAULT_ADDRESS_COPY.eyebrow;
  const title = copy?.title ?? DEFAULT_ADDRESS_COPY.title;
  const lede = copy?.lede ?? DEFAULT_ADDRESS_COPY.lede;
  const submitLabel = copy?.submitLabel ?? DEFAULT_ADDRESS_COPY.submitLabel;
  const pendingLabel = copy?.pendingLabel ?? DEFAULT_ADDRESS_COPY.pendingLabel;
  const [isLookingUpCep, setIsLookingUpCep] = useState(false);
  const [cepNotFound, setCepNotFound] = useState(false);
  const [cepUnavailable, setCepUnavailable] = useState(false);

  // Abort previous CEP lookup when a new one is triggered (race condition guard).
  const cepAbortRef = useRef<AbortController | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<AddressStepValues>({
    resolver: zodResolver(addressStepSchema),
    defaultValues: {
      type: initialValues?.type ?? "RESIDENTIAL",
      country: initialValues?.country ?? "Brasil",
      // With the usage section hidden the user cannot change these, so they stay `true`.
      useForBilling: showUsage ? (initialValues?.useForBilling ?? true) : true,
      useForDelivery: showUsage ? (initialValues?.useForDelivery ?? true) : true,
      zipCode: initialValues?.zipCode ?? "",
      street: initialValues?.street ?? "",
      neighborhood: initialValues?.neighborhood ?? "",
      city: initialValues?.city ?? "",
      state: initialValues?.state ?? "",
      number: initialValues?.number ?? "",
      complement: initialValues?.complement ?? "",
    },
  });

  // The step opens on the CEP, as the prototype does.
  useEffect(() => {
    setFocus("zipCode");
  }, [setFocus]);

  // The prototype's `ready`: the action waits for every required field and for the lookup.
  const [zipCode, street, number, neighborhood, city, state, country, useForBilling, useForDelivery] = useWatch({
    control,
    name: ["zipCode", "street", "number", "neighborhood", "city", "state", "country", "useForBilling", "useForDelivery"],
  });
  const isReady =
    onlyDigits(zipCode ?? "").length === 8 &&
    [street, number, neighborhood, city, state, country].every((value) => (value ?? "").trim() !== "") &&
    (useForBilling || useForDelivery) &&
    !isLookingUpCep;

  async function handleZipCodeChange(rawValue: string) {
    const digits = onlyDigits(rawValue).slice(0, 8);
    setValue("zipCode", digits);
    setCepNotFound(false);
    setCepUnavailable(false);

    // Clear fields from previous lookup when CEP changes.
    if (digits.length < 8) {
      // Abort any in-flight request and reset loading state.
      cepAbortRef.current?.abort();
      setIsLookingUpCep(false);
      setValue("street", "");
      setValue("neighborhood", "");
      setValue("city", "");
      setValue("state", "");
      return;
    }

    // Abort any in-flight lookup before starting a new one.
    cepAbortRef.current?.abort();
    const controller = new AbortController();
    cepAbortRef.current = controller;

    setIsLookingUpCep(true);
    const result = await lookupCep(digits, controller.signal);

    // If this request was aborted, a newer one took over — discard the result.
    if (controller.signal.aborted) return;

    if (!result || result.status === "unavailable") {
      setCepUnavailable(true);
    } else if (result.status === "found") {
      setValue("street", result.address.street);
      setValue("neighborhood", result.address.neighborhood);
      setValue("city", result.address.city);
      setValue("state", result.address.state);
    } else if (result.status === "not_found") {
      setCepNotFound(true);
    } else {
      setCepUnavailable(true);
    }
    setIsLookingUpCep(false);
    // The address came back: what is left to type is the number (the prototype's `numRef`).
    if (result?.status === "found") requestAnimationFrame(() => setFocus("number"));
  }

  return (
    <form data-clarity-mask="true"
      onSubmit={(e) => {
        // Prevent implicit submissions (browser autocomplete, Enter key on last field).
        // Only the explicit "Criar cadastro" button should trigger submit.
        const submitter = (e.nativeEvent as SubmitEvent).submitter;
        if (!submitter || submitter.getAttribute("data-explicit-submit") !== "true") {
          e.preventDefault();
          return;
        }
        handleSubmit(onSubmit)(e);
      }}
      className="flex w-full flex-col"
    >
      {onBack && <BackLink onClick={onBack}>{backLabel}</BackLink>}
      <StepHeader eyebrow={eyebrow} title={title} lede={lede} />

      <SectionEyebrow first>Tipo de endereço</SectionEyebrow>
      <Controller
        control={control}
        name="type"
        render={({ field }) => (
          // `@tb/segmented`'s default look (porcelain track) doesn't match
          // the product's solid-pill style, hence the class overrides below.
          <Segmented
            value={field.value}
            onValueChange={(value) => value && field.onChange(value)}
            aria-label="Tipo de endereço"
            className="w-fit gap-0 rounded-[var(--tb-radius-xs)] border border-[color:var(--tb-border-strong)] bg-transparent p-0"
          >
            {ADDRESS_TYPES.map((type, index) => (
              <SegmentedItem
                key={type}
                value={type}
                className={`min-h-0 rounded-none px-6 py-[11px] text-[12px] normal-case tracking-[0.06em] text-fg-muted data-[state=on]:bg-[var(--tb-oxide)] data-[state=on]:text-[var(--tb-porcelain)] data-[state=on]:shadow-none ${
                  index > 0 ? "border-l border-[color:var(--tb-border-strong)]" : ""
                }`}
              >
                {ADDRESS_TYPE_LABELS[type]}
              </SegmentedItem>
            ))}
          </Segmented>
        )}
      />

      <SectionEyebrow>Localização</SectionEyebrow>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 [@media(min-width:561px)]:grid-cols-2">
          <Controller
            control={control}
            name="zipCode"
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-2">
                <Label htmlFor="zipCode">CEP</Label>
                <div className="relative">
                  <Input
                    id="zipCode"
                    ref={field.ref}
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="00000-000"
                    value={maskZipCode(field.value)}
                    aria-invalid={!!errors.zipCode}
                    aria-describedby="zipCode-error"
                    onChange={(event) => handleZipCodeChange(event.target.value)}
                  />
                  {isLookingUpCep && (
                    <Spinner
                      size="sm"
                      label="Buscando endereço"
                      className="absolute right-0 top-1/2 -translate-y-1/2"
                    />
                  )}
                </div>
                <FieldHelp>
                  {isLookingUpCep
                    ? "Buscando o seu endereço…"
                    : cepNotFound
                      ? "Não encontramos esse CEP. Preencha o endereço manualmente."
                      : cepUnavailable
                        ? "Não foi possível buscar o CEP agora. Preencha manualmente."
                        : null}
                </FieldHelp>
                <FieldError id="zipCode-error">{errors.zipCode?.message}</FieldError>
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="street">Endereço</Label>
          {isLookingUpCep ? (
            <div className="h-[42px] rounded-[var(--tb-radius-xs)] tb-skeleton-shimmer" aria-hidden="true" />
          ) : (
            <Input id="street" {...register("street")} aria-invalid={!!errors.street} aria-describedby="street-error" />
          )}
          <FieldError id="street-error">{errors.street?.message}</FieldError>
        </div>

        <div className="grid grid-cols-1 gap-4 [@media(min-width:561px)]:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="number">Número</Label>
            <Input id="number" {...register("number")} aria-invalid={!!errors.number} aria-describedby="number-error" />
            <FieldError id="number-error">{errors.number?.message}</FieldError>
          </div>
          <div className="flex min-w-0 flex-col gap-2 [@media(min-width:561px)]:col-span-2">
            <Label htmlFor="complement">
              Complemento
              <OptionalHint />
            </Label>
            <Input id="complement" {...register("complement")} placeholder="Apto, bloco, referência" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 [@media(min-width:561px)]:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="neighborhood">Bairro</Label>
            {isLookingUpCep ? (
              <div className="h-[42px] rounded-[var(--tb-radius-xs)] tb-skeleton-shimmer" aria-hidden="true" />
            ) : (
              <Input id="neighborhood" {...register("neighborhood")} aria-invalid={!!errors.neighborhood} aria-describedby="neighborhood-error" />
            )}
            <FieldError id="neighborhood-error">{errors.neighborhood?.message}</FieldError>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="city">Cidade</Label>
            {isLookingUpCep ? (
              <div className="h-[42px] rounded-[var(--tb-radius-xs)] tb-skeleton-shimmer" aria-hidden="true" />
            ) : (
              <Input id="city" {...register("city")} aria-invalid={!!errors.city} aria-describedby="city-error" />
            )}
            <FieldError id="city-error">{errors.city?.message}</FieldError>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="state">Estado</Label>
            {isLookingUpCep ? (
              <div className="h-[42px] rounded-[var(--tb-radius-xs)] tb-skeleton-shimmer" aria-hidden="true" />
            ) : (
              <Input id="state" {...register("state")} aria-invalid={!!errors.state} aria-describedby="state-error" />
            )}
            <FieldError id="state-error">{errors.state?.message}</FieldError>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="country">País</Label>
          <Input id="country" {...register("country")} disabled />
        </div>
      </div>

      {showUsage && (
        <>
          <SectionEyebrow>Uso deste endereço</SectionEyebrow>
          <div className="flex flex-col gap-2">
            <div className="flex gap-3">
              <Controller
                control={control}
                name="useForBilling"
                render={({ field }) => (
                  <UsoChip checked={field.value} onCheckedChange={field.onChange}>
                    Cobrança
                  </UsoChip>
                )}
              />
              <Controller
                control={control}
                name="useForDelivery"
                render={({ field }) => (
                  <UsoChip checked={field.value} onCheckedChange={field.onChange}>
                    Entrega
                  </UsoChip>
                )}
              />
            </div>
            <FieldError id="useForBilling-error">{errors.useForBilling?.message}</FieldError>
          </div>
        </>
      )}

      <FieldError>{errorMessage}</FieldError>

      <Button type="submit" size="lg" disabled={isPending || !isReady} data-explicit-submit="true" className={`mt-7 w-full gap-2.5 ${STEP_CTA_CLASS}`}>
        {isPending ? pendingLabel : submitLabel}
        <ArrowRightIcon />
      </Button>
    </form>
  );
}
